import { FaSignOutAlt, FaChevronDown, FaSpinner } from 'react-icons/fa';
import { TbLayoutSidebarRightCollapseFilled } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { RiLockPasswordFill } from 'react-icons/ri';
import { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAuthenticate } from '@/context/AuthenticateContext';
import ChangePasswordForm from '@/components/ChangePasswordForm';
import useClickOutside from '@/@core/hooks/useClickOutside';
import { getPageTitle } from '@/router/utils/getPageTitle';
import SuspenseFallback from '@/@core/SuspenseFallback';
import { useLayout } from '@/context/AppAuthContext';
import { INITIAL_PATH } from '@/constants';
import { Modal } from '@/@core';
import useDocumentTitle from '@/@core/hooks/useDocumentTitle';

export const AppLayoutHeader = () => {
  const { broken, setToggled, _setCollapsed, collapsed } = useLayout();
  const { Auth: authenticate, logout, closingSession, setAuth, setIsLoggedIn } = useAuthenticate();
  const navigate = useNavigate();
  const location = useLocation();

  const { formatMessage: $t } = useIntl();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const userOptionsRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(userOptionsRef, () => isOpen && setIsOpen(false));

  const handleLogoutClick = async () => {
    if (closingSession) return;
    await logout();
    setAuth(undefined);
    setIsLoggedIn(false);
    navigate(`${INITIAL_PATH}/sign-in`);
    setIsOpen(false);
  };

  const userInitial = String(authenticate?.name || '')
    .charAt(0)
    .toUpperCase();
  const userName = String(authenticate?.name || '').toLocaleLowerCase();

  const currentTitle = useMemo(() => getPageTitle(location.pathname), [location.pathname]);
  useDocumentTitle(currentTitle);

  return (
    <>
      <Modal open={openModal} title="Change password" size="xxs" classesWrapper="p-2">
        <SuspenseFallback>
          <ChangePasswordForm
            onSuccess={() => setOpenModal(false)}
            onCancel={() => setOpenModal(false)}
          />
        </SuspenseFallback>
      </Modal>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="sticky top-0 z-50 flex min-h-[64px] w-full items-center justify-between border-b border-gray-200 bg-white px-4 shadow-sm dark:border-gray-700 dark:bg-zinc-800"
      >
        {/* Left Section - Sidebar Toggle */}
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center justify-center rounded-lg p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-primary dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-primary"
            onClick={
              broken ? () => setToggled((prev) => !prev) : () => _setCollapsed((prev) => !prev)
            }
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {broken ? (
              <motion.div
                key={collapsed ? 'expanded' : 'collapsed'}
                initial={{ rotate: -180 }}
                animate={{ rotate: 0 }}
                transition={{ duration: 0.2 }}
              >
                <TbLayoutSidebarRightCollapseFilled size={22} />
              </motion.div>
            ) : (
              <motion.div
                key={collapsed ? 'collapsed' : 'expanded'}
                initial={{ rotate: -180 }}
                animate={{ rotate: 0 }}
                transition={{ duration: 0.2 }}
              >
                <TbLayoutSidebarRightCollapseFilled size={22} />
              </motion.div>
            )}
          </motion.button>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{currentTitle}</p>
        </div>

        {/* Right Section - User Menu */}
        <div className="flex items-center gap-3">
          <div className="relative" ref={userOptionsRef}>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="button"
              className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              id="options-menu"
              aria-haspopup="true"
              aria-expanded={isOpen}
              onClick={() => setIsOpen(!isOpen)}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70 text-xs font-bold capitalize text-white shadow-sm">
                {userInitial}
              </div>
              <span className="hidden max-w-[120px] overflow-hidden text-ellipsis text-nowrap capitalize sm:block">
                {userName}
              </span>
              <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <FaChevronDown size={12} className="text-gray-500" />
              </motion.div>
            </motion.button>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 z-[100] mt-2 w-64 origin-top-right overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-200 dark:bg-zinc-800 dark:ring-gray-700"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="options-menu"
                >
                  {/* User Info Section */}
                  <div className="border-b border-gray-200 bg-gradient-to-r from-primary/5 to-primary/10 px-4 py-4 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary/70 text-lg font-bold text-white shadow-md">
                        {userInitial}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold capitalize text-gray-900 dark:text-white">
                          {userName}
                        </p>
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {authenticate.email || 'User'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    <motion.button
                      whileHover={{ x: 4 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={() => {
                        setOpenModal(true);
                        setIsOpen(false);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 hover:text-primary dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-primary"
                      role="menuitem"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <RiLockPasswordFill size={16} />
                      </div>
                      <span className="font-medium">Change Password</span>
                    </motion.button>

                    <motion.button
                      whileHover={{ x: 4 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={handleLogoutClick}
                      disabled={closingSession}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-gray-700 transition-colors hover:bg-danger/10 hover:text-danger disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:bg-danger/20"
                      role="menuitem"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-danger/10 text-danger">
                        {closingSession ? (
                          <FaSpinner className="animate-spin" size={16} />
                        ) : (
                          <FaSignOutAlt size={16} />
                        )}
                      </div>
                      <span className="font-medium">
                        {!closingSession ? $t({ id: 'Logout' }) : 'Closing...'}
                      </span>
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.header>
    </>
  );
};
